<template>
  <div
    class="flex flex-col h-full w-full"
    :class="[mobile ? 'left-0' : '-left-72 sm:left-0']"
  >
    <!-- Logo + collapse toggle. Same chrome as before — pure presentation,
         independent of how the nav items render below. -->
    <div
      class="p-4 flex items-center justify-between"
      :class="[collapse ? 'sm:flex-col sm:items-center' : '']"
    >
      <img
        :class="[collapse ? 'sm:hidden' : '']"
        :src="menuLogoFull"
        class="block h-6 sm:h-8 opacity-85 -ml-1"
      >
      <img
        :class="[collapse ? 'hidden sm:block' : 'hidden']"
        :src="menuLogoIcon"
        class="block w-6 sm:w-8 opacity-85 mb-6"
      >
      <button
        class="hidden sm:flex opacity-85 hover:bg-white/15 p-2 rounded-md transition-all"
        :class="[collapse && !mobile ? 'ml-px' : '0']"
        @click="uiStore.toggleMenu()"
      >
        <UIcon name="lucide:menu" :size="18" class="text-white opacity-75" />
      </button>
      <button
        class="flex sm:hidden opacity-85 hover:bg-white/15 p-2 rounded-md transition-all"
        :class="[collapse && !mobile ? 'ml-1' : '0']"
        @click="uiStore.toggleMobile()"
      >
        <UIcon name="lucide:x" :size="18" class="text-white opacity-75" />
      </button>
    </div>

    <!-- Search pill — opens the universal search modal. -->
    <div class="px-4 pt-2 pb-0">
      <div
        class="font-medium rounded-lg bg-white/10 dark:bg-gray-800 flex items-center text-white text-base cursor-pointer hover:bg-white/15 hover:text-white active:text-white transition leading-none border border-white/20"
        :class="[
          collapse && !mobile ? '-ml-px py-3 px-3 sm:p-0 sm:h-8 sm:w-9 sm:justify-center' : 'py-2 px-1',
        ]"
        @click="uiStore.toggleSearch()"
      >
        <o-icon
          icon="search"
          :size="14"
          :class="[collapse && !mobile ? '' : 'mr-3 ml-1']"
        />
        <span
          :class="[
            collapse && !mobile ? 'sm:hidden' : '',
            'font-medium',
          ]"
        >
          {{ $t('general.search') }}
        </span>
      </div>
    </div>

    <!-- Primary nav. UNavigationMenu drives routing, active state, badges,
         and collapsed-mode icon-only rendering. MessageCount is opted in
         via `slot: 'message'` and the trailing template below. -->
    <o-scroll class="grow shrink h-10">
      <div class="px-2 pt-3">
        <UNavigationMenu
          :items="primaryItems"
          orientation="vertical"
          :collapsed="collapse && !mobile"
          :ui="{
            link: 'group py-2 px-4',
            linkLabel: 'text-white/80 dark:text-white/80',
            linkLeadingIcon: 'text-white/70 dark:text-white/70 size-4 group-hover:text-white',
            linkTrailingIcon: 'text-white/60 dark:text-white/60 size-3.5',
          }"
        >
          <template #message-trailing>
            <MessageCount />
          </template>
        </UNavigationMenu>
      </div>
    </o-scroll>

    <!-- Secondary nav. Folds the holiday animation toggle, alerts, help,
         and support into a single UNavigationMenu — same visual treatment
         as the primary block above. -->
    <div v-if="secondaryItems.length" class="shrink-0 px-2 pb-2">
      <UNavigationMenu
        :items="secondaryItems"
        orientation="vertical"
        :collapsed="collapse && !mobile"
        :ui="{
          link: 'group py-2 px-4',
          linkLabel: 'text-white/80 dark:text-white/80',
          linkLeadingIcon: 'text-white/70 dark:text-white/70 size-4 group-hover:text-white',
          linkTrailingIcon: 'text-white/60 dark:text-white/60 size-3.5',
        }"
      >
        <!-- Holiday animation toggle — emoji icon, label flips based on
             whether the animation is currently running. -->
        <template #holiday-leading="{ active }">
          <span class="text-base leading-none">
            {{ active ? '🥶' : '❄️' }}
          </span>
        </template>
      </UNavigationMenu>
    </div>

    <!-- Profile footer. Account stays outside the nav — it's a profile
         widget with a dropdown, not a destination link. -->
    <div class="shrink-0 w-full text-white opacity-85 px-4 py-2 border-t border-white/10">
      <layout-account />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { NavigationMenuItem } from '@nuxt/ui'

import MessageCount from './MessageCount.vue'

import { useUIStore } from '~/stores/useUIStore'
import { useAlertStore } from '~/stores/useAlertStore'
import { settingsNavigationItemMatchesPath } from '@/lib/settingsSearch'

import { groups as manageGroups } from '@/components/manage/pages'
import { groups as minisiteGroups } from '@/components/minisite/pages'
import { groups as accountGroups } from '@/components/account/pages'

import logoFull from '~/assets/images/logo/logo-white-full.png'
import logoIcon from '~/assets/images/logo/logo-white-icon.png'

const { $auth, $holiday, $grants, $gates, $panel } = useNuxtApp()
const runtimeConfig = useRuntimeConfig()
const uiStore = useUIStore()
const alertStore = useAlertStore()
const route = useRoute()
const { t } = useI18n()
const canAccess = useAccess()

const collapse = computed(() => uiStore.menu)
const mobile = computed(() => uiStore.mobile)
const demo = computed(() => runtimeConfig.public.instance === 'demo')

const alertCounts = computed(() => alertStore.counts)

const activeHoliday = computed(() => ($holiday as any)?.getActive())
const holidayLogo = computed(() => activeHoliday.value?.logo || null)
const menuLogoFull = computed(() => resolveImageSrc(holidayLogo.value?.full ?? logoFull))
const menuLogoIcon = computed(() => resolveImageSrc(holidayLogo.value?.icon ?? logoIcon))

// Show the holiday toggle whenever the active holiday config declares a
// menu-button component AND the animation slot is enabled. Currently only
// Christmas opts in; new holidays inherit the same gating.
const showHolidayItem = computed(() =>
  !!(activeHoliday.value?.animation?.menuButton?.component
    && activeHoliday.value?.animation?.enabled),
)

const holidayActive = computed<boolean>(() => !!(uiStore as any).holidayAnimation)

// Christmas-specific copy. Holds inline because Christmas is the only
// holiday with a button today; if more holidays gain buttons, lift the
// labels into the holiday config (e.g. `animation.menuButton.labels`).
const holidayLabel = computed(() => holidayActive.value ? "I'm getting cold!" : 'Let it snow!')

onMounted(() => alertStore.fetchAlertCounts())

function resolveImageSrc(image: unknown): string {
  if (typeof image === 'string' && image.length) return image
  if (image && typeof image === 'object') {
    const source = image as Record<string, unknown>
    const value = source.src ?? source.default ?? source.url ?? source.path
    if (typeof value === 'string' && value.length) return value
  }
  return ''
}

// ---- Nav-item config -------------------------------------------------------

interface RawItem {
  /** Route to navigate to. */
  url?: string
  /** Translation key for the label. */
  name: string
  /** Iconify name (e.g. `'lucide:layout-dashboard'`). */
  icon: string
  /** Path-prefix match for active-state. Defaults to exact `url` match. */
  match?: string
  /** Required grants (any of these must be granted). */
  grants?: Record<string, string[]>
  /** Required feature gates. */
  gates?: string[]
  /** Restrict to adminUser sessions. */
  admin?: boolean
  /** External link — opens in a new tab. */
  external?: boolean
  /** Stable slot key — pairs with `#${slot}-trailing` / `-leading` templates. */
  slot?: string
  /** Settings section link — resolves to the first accessible page in it. */
  settingsSection?: SettingsMode
}

type SettingsMode = 'manage' | 'minisite' | 'account'

interface SettingsItem {
  path: string
  aliases?: string[]
  children?: SettingsItem[]
}

interface SettingsGroup {
  items: SettingsItem[]
}

const SETTINGS_CONFIGS: Record<SettingsMode, SettingsGroup[]> = {
  manage: manageGroups as SettingsGroup[],
  minisite: minisiteGroups as SettingsGroup[],
  account: accountGroups as SettingsGroup[],
}

const primaryRaw: RawItem[] = [
  { url: '/',          name: 'general.dashboard',    icon: 'lucide:layout-dashboard' },
  { url: '/schedule',  name: 'general.appointments', icon: 'lucide:calendar',       match: '/schedule', grants: { reservation: ['view'] } },
  { url: '/order',     name: 'general.sales',        icon: 'lucide:receipt',                            grants: { order: ['view'] } },
  { url: '/customer',  name: 'general.customers',    icon: 'lucide:users',          match: '/customer', grants: { customer: ['profile'] } },
  { url: '/roster',    name: 'general.roster',       icon: 'lucide:calendar-days',                      grants: { roster: ['view'] } },
  { url: '/marketing', name: 'general.marketing',    icon: 'lucide:megaphone',                          grants: { marketing: ['view'] } },
  { url: '/message',   name: 'general.messages',     icon: 'lucide:message-square',                     grants: { marketing: ['view'] }, gates: ['messaging'], slot: 'message' },
  { url: '/report',    name: 'general.reports',      icon: 'lucide:bar-chart-3',    match: '/report',   grants: { report: ['view'] } },
  { url: '/manage/business/account', name: 'general.manage', icon: 'lucide:settings', settingsSection: 'manage' },
  { url: '/minisite/preferences', name: 'general.minisite', icon: 'lucide:globe', settingsSection: 'minisite' },
  { url: '/account/account', name: 'general.account', icon: 'lucide:user-circle', settingsSection: 'account' },
]

const helpItem: RawItem = {
  url: 'https://ovatu.com/help/next/guides',
  name: 'components.layout.help',
  icon: 'lucide:life-buoy',
  external: true,
}

const hasVisibleSettingsItems = (items: SettingsItem[]): boolean =>
  items.some((item) => canAccess(item.path) || hasVisibleSettingsItems(item.children ?? []))

const hasVisibleSettingsSection = (section: SettingsMode): boolean =>
  SETTINGS_CONFIGS[section].some((group) => hasVisibleSettingsItems(group.items))

const firstVisibleSettingsPath = (items: SettingsItem[]): string | null => {
  for (const item of items) {
    if (canAccess(item.path)) return item.path
    const child = firstVisibleSettingsPath(item.children ?? [])
    if (child) return child
  }
  return null
}

const firstVisibleSettingsSectionPath = (section: SettingsMode): string | null =>
  firstVisibleSettingsPath(SETTINGS_CONFIGS[section].flatMap((group) => group.items))

const settingsSectionContainsPath = (items: SettingsItem[], path: string): boolean =>
  items.some((item) =>
    settingsNavigationItemMatchesPath(item, path) || settingsSectionContainsPath(item.children ?? [], path),
  )

// Mirrors the access logic from the old MenuItem.vue. Combines
// route-declared grants/gates (via the plugin lookup) with item-declared
// ones so static nav configs and dynamic route policies stay in sync.
const allowed = (item: RawItem): boolean => {
  if (item.admin && !($auth as any).info?.adminUser) return false
  if (item.settingsSection) return hasVisibleSettingsSection(item.settingsSection)
  if (!item.url) return true
  if (item.external) return true
  const path = item.match || item.url
  const grants = ($grants as any).route(path) || item.grants
  const gates = ($gates as any).route(path) || item.gates
  if (!grants && !gates) return canAccess(path)
  return ($grants as any).all(grants || []) && ($gates as any).all(gates || []) && canAccess(path)
}

const isActive = (item: RawItem): boolean => {
  if (item.settingsSection) {
    return SETTINGS_CONFIGS[item.settingsSection].some((group) =>
      settingsSectionContainsPath(group.items, route.fullPath.replace(/#.*$/, '')),
    )
  }
  if (!item.url) return false
  const path = route.path
  return item.url === path || (!!item.match && path.startsWith(item.match))
}

const toNavItem = (item: RawItem): NavigationMenuItem | null => {
  if (!allowed(item)) return null

  const nav: NavigationMenuItem = {
    label: t(item.name),
    icon: item.icon,
    slot: item.slot,
    active: isActive(item),
    onSelect: () => uiStore.setMobile(false),
  }

  if (item.external) {
    nav.to = item.url
    nav.target = '_blank'
  } else if (item.settingsSection) {
    nav.to = firstVisibleSettingsSectionPath(item.settingsSection) ?? item.url
  } else if (item.url) {
    nav.to = item.url
  }

  return nav
}

const primaryItems = computed<NavigationMenuItem[]>(() =>
  primaryRaw.map(toNavItem).filter((i): i is NavigationMenuItem => i !== null),
)

// Secondary nav assembles in display order: holiday (conditional), alerts
// (with badge), help (external), support.
const secondaryItems = computed<NavigationMenuItem[]>(() => {
  const items: NavigationMenuItem[] = []

  if (showHolidayItem.value) {
    items.push({
      label: holidayLabel.value,
      slot: 'holiday',
      active: holidayActive.value,
      onSelect: () => (uiStore as any).toggleHolidayAnimation(),
    })
  }

  items.push({
    label: t('general.alerts'),
    icon: 'lucide:bell',
    active: route.hash === '#/alerts',
    badge: alertCounts.value?.unread ? Number(alertCounts.value.unread) : undefined,
    onSelect: () => {
      uiStore.setMobile(false)
      ;($panel as any).create('/alerts', { props: {} })
    },
  })

  const help = toNavItem(helpItem)
  if (help) items.push(help)

  if (!demo.value) {
    items.push({
      label: t('components.layout.support.title'),
      icon: 'lucide:message-circle',
      onSelect: () => {
        if ((window as any).Beacon) (window as any).Beacon('toggle')
      },
    })
  }

  return items
})
</script>
